import React, { useRef, useState } from 'react'
import Papa from 'papaparse'
import API from '../../../services/API'
import downloadCsv from '../../../helpers/downloadCsv'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { CloudUpload } from '@mui/icons-material'

const requiredColumns = ['First Name', 'Last Name', 'Email Address']

// Header + example row for the downloadable template. Generated client-side
// (see helpers/downloadCsv.js) — a static file under public/ is never served.
const TEMPLATE_ROWS = [
  // prettier-ignore
  [
    'Prefix', 'First Name', 'Last Name', 'Email Address', 'Pin Number',
    'Clinical Role', 'Teams', 'Enabled Roles', 'Date of Birth', 'Pronouns',
    'National Insurance', 'Nationality', 'Birthplace', 'Address Line 1',
    'Address Line 2', 'Town', 'City', 'County', 'Post Code', 'Phone Number 1',
    'Phone Number 2', 'Emergency Contact First Name',
    'Emergency Contact Last Name', 'Emergency Contact Phone 1',
    'Emergency Contact Phone 2', 'Emergency Contact Relationship',
    'Driving Licence Number', 'Has C1', 'Permission to Drive',
    'Licence Expiry', 'Employment Type', 'BACS Name', 'BACS Account Number',
    'BACS Sort Code', 'Registered Healthcare Professional', 'Regulatory Body',
    'Registration Number', 'Renewal Date', 'DBS Certificate', 'DBS Date',
    'DBS Update Service', 'Driving Licence Check Code',
    'Driving Licence Check', 'Base Locations',
  ],
  // prettier-ignore
  [
    'Mr', 'Test', 'Employee', 'test.employee@example.com', 'TEST001',
    'First Responder', 'Events', 'Employee', '01/01/1990', 'He/Him',
    'QQ123456C', 'British', 'Manchester', '1 Test Street', 'Flat 2',
    'Testford', 'Manchester', 'Greater Manchester', 'TE1 1ST', '07123456789',
    '07987654321', 'Jane', 'Employee', '07000000000', '', 'Partner',
    'TESTDRIVE123', 'Yes', 'Yes', '31/12/2030', 'Employee', 'Test Employee',
    '12345678', '12-34-56', 'Yes', 'HCPC', 'PA12345', '31/12/2030',
    'DBS123456', '01/01/2026', 'No', 'CHECK123', '01/01/2026', 'Default',
  ],
]

const getValue = (row, ...keys) =>
  keys
    .map((key) => row[key])
    .find((value) => typeof value === 'string' && value.trim().length > 0)
    ?.trim() || ''

const isEmptyImportRow = (row) =>
  Object.values(row).every(
    (value) => value === null || value === undefined || String(value).trim() === '',
  )

const splitName = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)

  if (parts.length === 0) return { firstName: '', lastName: '' }
  if (parts.length === 1) return { firstName: parts[0], lastName: '' }

  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts[parts.length - 1],
  }
}

const normaliseYesNo = (value) => {
  const normalised = String(value || '').trim().toLowerCase()
  return normalised.startsWith('yes') ? 'Yes' : value || ''
}

const mapJotformEmployeeImportRow = (row) => {
  const employeeName = splitName(row.Name)
  const nokName = splitName(row.Name_1)
  const fullName = getValue(row, 'Name')

  return {
    ...row,
    'First Name': employeeName.firstName,
    'Last Name': employeeName.lastName,
    'Email Address': getValue(row, 'Email'),
    Prefix: getValue(row, 'Title (e.g. Mr, Miss, Mrs, Dr, Ms, Mx)'),
    Pronouns: getValue(
      row,
      'Proffered Pronouns (i.e He/Him, She/Her, They/Them, other)',
    ),
    'Date of Birth': getValue(row, 'Date of Birth'),
    Nationality: getValue(row, 'Nationality'),
    'National Insurance': getValue(row, 'National Insurance Number'),
    'Address Line 1': getValue(row, 'Current Address'),
    'Phone Number 1': getValue(row, 'Phone number'),
    'Emergency Contact First Name': nokName.firstName,
    'Emergency Contact Last Name': nokName.lastName,
    'Emergency Contact Phone 1': getValue(row, 'Telephone Number'),
    'Emergency Contact Relationship': getValue(row, 'Relationship to you'),
    'BACS Name': fullName,
    'BACS Account Number': getValue(row, 'Account Number'),
    'BACS Sort Code': getValue(row, 'Sort Code'),
    'Clinical Role': getValue(row, 'Clinical qualification / grade'),
    'Registered Healthcare Professional': getValue(
      row,
      'HCPC/GMC/NMC registration number (healthcare professionals only)',
    )
      ? 'Yes'
      : '',
    'Registration Number': getValue(
      row,
      'HCPC/GMC/NMC registration number (healthcare professionals only)',
    ),
    'DBS Certificate': getValue(row, 'DBS Certificate or update service Number.'),
    'Permission to Drive': normaliseYesNo(
      getValue(row, 'Do you hold a full UK manual driving licence'),
    ),
    'Has C1': getValue(row, 'Do you hold a full UK manual driving licence')
      .toLowerCase()
      .includes('c1')
      ? 'Yes'
      : '',
  }
}

export const mapEmployeeImportRow = (row, idx) => {
  const mapped =
    row['First Name'] || row['Email Address']
      ? row
      : mapJotformEmployeeImportRow(row)

  return {
    ...mapped,
    rowNumber: idx + 2, // +2 because header row is 1
    firstName: (mapped['First Name'] || '').trim(),
    lastName: (mapped['Last Name'] || '').trim(),
    email: (mapped['Email Address'] || '').trim().toLowerCase(),
    employeeID: (mapped['Pin Number'] || '').trim(),
  }
}

export const validateEmployeeImportRows = (rows, metaFields = []) => {
  const rowErrors = []
  const isJotformExport = metaFields.includes('Email') && metaFields.includes('Name')
  const missingColumns = isJotformExport
    ? []
    : requiredColumns.filter((column) => !metaFields.includes(column))

  missingColumns.forEach((column) =>
    rowErrors.push(`Missing required column: ${column}`),
  )

  rows.forEach((row) => {
    if (!row.firstName)
      rowErrors.push(`Row ${row.rowNumber}: missing First Name`)
    if (!row.lastName) rowErrors.push(`Row ${row.rowNumber}: missing Last Name`)
    if (!row.email) rowErrors.push(`Row ${row.rowNumber}: missing Email Address`)
  })

  return rowErrors
}

export const parseEmployeeImportCsv = (file) =>
  new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim().replace(/^\uFEFF/, ''),
      complete: (results) => {
        const rows = results.data
          .filter((row) => !isEmptyImportRow(row))
          .map(mapEmployeeImportRow)
        const errors = validateEmployeeImportRows(rows, results.meta.fields)

        resolve({ rows, errors })
      },
      error: (err) => reject(err),
    })
  })

export const uploadEmployeeImportRows = async ({ tenantID, rows }) => {
  const res = await API.post('/admincrm/employees/bulkupload', {
    tenantID,
    employees: rows,
  })

  if (res.status !== 200) {
    throw new Error(res.data?.message || res.data?.error || 'Upload failed')
  }

  return res.data
}

const BulkEmployeeImport = ({ tenantID }) => {
  const [rows, setRows] = useState([])
  const [errors, setErrors] = useState([])
  const [result, setResult] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [fileName, setFileName] = useState('')
  const fileInputRef = useRef(null)

  const parseCsv = async (file) => {
    setResult(null)
    setRows([])
    setErrors([])
    setFileName(file.name)

    try {
      const parsed = await parseEmployeeImportCsv(file)
      setRows(parsed.rows)
      setErrors(parsed.errors)
    } catch (err) {
      setErrors([String(err)])
    }
  }

  const upload = async () => {
    setErrors([])
    setResult(null)

    if (rows.length === 0) {
      setErrors(['No rows parsed.'])
      return
    }

    setUploading(true)

    try {
      const uploadResult = await uploadEmployeeImportRows({ tenantID, rows })
      setResult(uploadResult)
      if (uploadResult?.errors?.length) {
        setErrors(
          uploadResult.errors.map(
            (err) =>
              `Row ${(err.index ?? 0) + 2}: ${err.reason || 'Upload failed'}`,
          ),
        )
      }
    } catch (e) {
      setErrors([e?.message || 'Upload Failed'])
      console.error(e)
    } finally {
      setUploading(false)
    }
  }

  return (
    <Paper>
      <Stack padding={2} gap={1}>
        <Typography variant="title2">Bulk Employee Import</Typography>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) parseCsv(file)
            e.target.value = ''
          }}
          style={{ display: 'none' }}
          ref={fileInputRef}
        />
        <Box
          sx={{
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'grey.400',
            bgcolor: 'grey.50',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'anBlue.main',
              bgcolor: 'grey.100',
            },
          }}
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          <CloudUpload sx={{ fontSize: 32, mb: 1, color: 'anBlue.main' }} />
          <Typography variant="body2" fontWeight={600}>
            Select File
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {fileName || 'CSV Only'}
          </Typography>
          {uploading && <LinearProgress />}
        </Box>
        <Button
          onClick={() =>
            downloadCsv(
              'employee-bulk-import-template.csv',
              Papa.unparse(TEMPLATE_ROWS),
            )
          }
          size="small"
          variant="text"
          sx={{ alignSelf: 'flex-start' }}
        >
          Download Template
        </Button>
        <Typography variant="body2">
          <strong>Parsed:</strong> {rows.length} rows
        </Typography>
        {result && (
          <Alert severity={result.failed > 0 ? 'warning' : 'success'}>
            <AlertTitle>Upload Complete</AlertTitle>
            Created {result.created} employees. Failed {result.failed}.
          </Alert>
        )}
        {errors.length > 0 && (
          <Alert severity="error" variant="filled">
            <AlertTitle>Issues:</AlertTitle>

            <ul>
              {errors.slice(0, 20).map((er, i) => (
                <li key={i}>{er}</li>
              ))}
            </ul>
            {errors.length > 20 && <div>...and {errors.length - 20} more</div>}
          </Alert>
        )}

        {rows.length > 0 && (
          <>
            <h3 style={{ marginTop: 16 }}>Preview (first 5)</h3>
            <pre
              style={{ background: '#f6f6f6', padding: 12, borderRadius: 8 }}
            >
              {JSON.stringify(rows.slice(0, 5), null, 2)}
            </pre>
            <Button
              onClick={upload}
              disabled={errors.length > 0 || uploading}
              variant="contained"
              color="primary"
              sx={{ alignSelf: 'flex-start', mt: 1 }}
            >
              {uploading ? 'Uploading...' : 'Upload to Tenant'}
            </Button>
          </>
        )}
      </Stack>
    </Paper>
  )
}

export default BulkEmployeeImport
